/**
 * Internet Connectivity Service
 *
 * Provides centralized internet connectivity detection for the entire app.
 * Combines navigator.onLine detection with active connection testing.
 */

import eventBus from '../utils/eventBus';

// Consistent log prefix for easy filtering
const LOG_PREFIX = '[INTERNET_CONNECTIVITY]';

class InternetConnectivityService {
    constructor() {
        this.isOnline = typeof navigator !== 'undefined' ? navigator.onLine : true;
        this.hasInternet = this.isOnline;
        this.listeners = new Set();
        this.checkingConnection = false;
        this.lastCheckTime = 0;
        this.initialCheckDone = false;
        this.checkInterval = 5000;
        this.timeoutDuration = 3000;

        console.log(`${LOG_PREFIX} Service initialized - Navigator online: ${this.isOnline}, hasInternet (optimistic): ${this.hasInternet}`);

        this.initializeEventListeners();
        this.checkInternetConnection(true);
    }

    /**
     * Initialize browser online/offline event listeners
     */
    initializeEventListeners() {
        if (typeof window === 'undefined') {
            console.log(`${LOG_PREFIX} Skipping event listeners - no window object`);
            return;
        }

        console.log(`${LOG_PREFIX} Setting up browser online/offline event listeners`);

        const updateOnlineStatus = () => {
            const wasOnline = this.isOnline;
            this.isOnline = navigator.onLine;

            console.log(`${LOG_PREFIX} Navigator online status changed: ${wasOnline} → ${this.isOnline}`);

            if (this.isOnline && !wasOnline) {
                console.log(`${LOG_PREFIX} Just came online - testing connection`);
                this.checkInternetConnection();
            } else if (!this.isOnline) {
                console.log(`${LOG_PREFIX} Went offline - marking as no internet`);
                this.hasInternet = false;
                this.notifyListeners();
            }
        };

        window.addEventListener('online', updateOnlineStatus);
        window.addEventListener('offline', updateOnlineStatus);

        // Store references for cleanup
        this.onlineHandler = updateOnlineStatus;
        console.log(`${LOG_PREFIX} Event listeners registered successfully`);
    }

    /**
     * Test actual internet connectivity by making a request
     * Uses multiple fallback endpoints for reliability
     */
    async checkInternetConnection(force = false) {
        const now = Date.now();
        if (!force && this.checkingConnection) {
            return this.hasInternet;
        }

        if (!force && this.initialCheckDone && (now - this.lastCheckTime) < this.checkInterval) {
            return this.hasInternet;
        }

        this.checkingConnection = true;
        this.lastCheckTime = now;

        if (!this.isOnline) {
            console.log(`${LOG_PREFIX} Navigator reports offline, skipping connection test`);
            this.hasInternet = false;
            this.checkingConnection = false;
            this.initialCheckDone = true;
            this.notifyListeners();
            return false;
        }

        const testEndpoints = [
            'https://www.google.com/generate_204',
            'https://api.promptus.ai/api/generate/get-models',
            'https://api.dev.promptus.ai/api/generate/get-models'
        ];

        for (const endpoint of testEndpoints) {
            try {
                const controller = new AbortController();
                const timeoutId = setTimeout(() => controller.abort(), this.timeoutDuration);

                const response = await fetch(endpoint, {
                    method: 'HEAD',
                    mode: 'no-cors',
                    cache: 'no-cache',
                    signal: controller.signal
                });

                clearTimeout(timeoutId);

                const hadInternet = this.hasInternet;
                this.hasInternet = true;
                this.checkingConnection = false;
                this.initialCheckDone = true;
                if (!hadInternet) this.notifyListeners();
                return true;

            } catch (error) {
                continue;
            }
        }

        const hadInternet = this.hasInternet;
        this.hasInternet = false;
        this.checkingConnection = false;
        this.initialCheckDone = true;
        if (hadInternet) this.notifyListeners();
        return false;
    }

    /**
     * Get current connectivity status
     * @returns {Object} { isOnline: boolean, hasInternet: boolean, isConnected: boolean }
     */
    getStatus() {
        return {
            isOnline: this.isOnline,           // Navigator online/offline
            hasInternet: this.hasInternet,     // Actual internet connectivity
            isConnected: this.isOnline && this.hasInternet  // Both conditions met
        };
    }

    /**
     * Check if the app should bypass internet-dependent features
     * @returns {boolean} true if internet features should be bypassed
     */
    shouldBypassInternetFeatures() {
        return !this.hasInternet;
    }

    /**
     * Check if the app is completely offline
     * @returns {boolean} true if completely offline
     */
    isCompletelyOffline() {
        return !this.isOnline || !this.hasInternet;
    }

    /**
     * Force a connectivity check regardless of rate limiting
     * @returns {Promise<boolean>} true if internet is available
     */
    async forceCheck() {
        return await this.checkInternetConnection(true);
    }

    /**
     * Subscribe to connectivity changes
     * @param {Function} callback - Called with { isOnline, hasInternet, isConnected }
     * @returns {Function} unsubscribe function
     */
    subscribe(callback) {
        this.listeners.add(callback);

        // Immediately call with current status
        callback(this.getStatus());

        // Return unsubscribe function
        return () => {
            this.listeners.delete(callback);
        };
    }

    /**
     * Notify all subscribers of connectivity changes
     */
    notifyListeners() {
        const status = this.getStatus();
        console.log(`${LOG_PREFIX} Status update - Online: ${status.isOnline}, Internet: ${status.hasInternet}, Connected: ${status.isConnected}`);

        this.listeners.forEach(callback => {
            try {
                callback(status);
            } catch (error) {
                console.error(`${LOG_PREFIX} Error in listener callback:`, error);
            }
        });

        eventBus.emit('connectivityChanged', status);
    }

    /**
     * Clean up event listeners
     */
    destroy() {
        if (typeof window !== 'undefined' && this.onlineHandler) {
            window.removeEventListener('online', this.onlineHandler);
            window.removeEventListener('offline', this.onlineHandler);
        }
        this.listeners.clear();
    }

    /**
     * Start periodic connectivity monitoring
     * @param {number} interval - Check interval in milliseconds (default: 30000)
     */
    startMonitoring(interval = 30000) {
        this.stopMonitoring();

        this.monitoringInterval = setInterval(() => {
            if (this.isOnline) {
                this.checkInternetConnection();
            }
        }, interval);
    }

    /**
     * Stop periodic connectivity monitoring
     */
    stopMonitoring() {
        if (this.monitoringInterval) {
            clearInterval(this.monitoringInterval);
            this.monitoringInterval = null;
        }
    }
}

// Create and export singleton instance
const internetConnectivityService = new InternetConnectivityService();

export default internetConnectivityService;

// Export class for testing
export { InternetConnectivityService };